/**
 * FavGreen theme components.
 *
 *   <fg-scroller> – horizontal product row: arrow buttons and mouse drag.
 *   <fg-story>    – craft story: shows the image for the step in the middle of the viewport.
 *   [data-fg-dropdown] – header menu link that opens its collections list on click instead of navigating.
 */

const scrollBehavior = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

class FgScroller extends HTMLElement {
  #startX = 0;
  #startScroll = 0;
  #dragged = false;

  connectedCallback() {
    this.root = this.closest('.shopify-section, section') ?? this.parentElement;
    this.buttons = [...(this.root?.querySelectorAll('[data-fg-scroll]') ?? [])];
    this.buttons.forEach((button) =>
      button.addEventListener('click', () => this.#step(Number(button.getAttribute('data-fg-scroll'))))
    );

    this.addEventListener('scroll', this.#update, { passive: true });
    this.addEventListener('pointerdown', this.#onDown);
    this.addEventListener('click', this.#onClick, true);
    this.resizeObserver = new ResizeObserver(this.#update);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  /** @param {number} direction */
  #step(direction) {
    const card = this.querySelector('li');
    if (!(card instanceof HTMLElement) || !card.parentElement) return;
    const gap = parseFloat(getComputedStyle(card.parentElement).columnGap) || 0;
    this.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: scrollBehavior() });
  }

  #update = () => {
    const max = this.scrollWidth - this.clientWidth;
    this.buttons?.forEach((button) => {
      const direction = Number(button.getAttribute('data-fg-scroll'));
      /** @type {HTMLButtonElement} */ (button).disabled =
        max <= 1 || (direction < 0 ? this.scrollLeft <= 1 : this.scrollLeft >= max - 1);
    });
  };

  /** @param {PointerEvent} event */
  #onDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.#startX = event.clientX;
    this.#startScroll = this.scrollLeft;
    this.#dragged = false;
    window.addEventListener('pointermove', this.#onMove);
    window.addEventListener('pointerup', this.#onUp, { once: true });
  };

  /** @param {PointerEvent} event */
  #onMove = (event) => {
    const delta = event.clientX - this.#startX;
    if (!this.#dragged && Math.abs(delta) < 6) return;
    this.#dragged = true;
    this.classList.add('is-dragging');
    this.scrollLeft = this.#startScroll - delta;
  };

  #onUp = () => {
    window.removeEventListener('pointermove', this.#onMove);
    this.classList.remove('is-dragging');
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#dragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.#dragged = false;
  };
}

/**
 * Sticky story: highlights the step closest to the middle of the viewport and swaps the pinned image,
 * playing only the video that is on screen.
 */
class FgStory extends HTMLElement {
  connectedCallback() {
    const steps = [...this.querySelectorAll('.fg-story__step')];
    const media = [...this.querySelectorAll('.fg-story__media-item')];

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = steps.indexOf(/** @type {Element} */ (entry.target));
          steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
          media.forEach((item, i) => item.classList.toggle('is-active', i === index));
          this.style.setProperty('--fg-story-step', String(index));
          this.#syncVideos();
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );

    steps.forEach((step) => this.observer?.observe(step));

    // Only one layout is visible at a time: the pinned media on desktop, the step media on mobile.
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.mobileQuery.addEventListener('change', this.#syncVideos);
    this.#syncVideos();
  }

  #syncVideos = () => {
    const mobile = this.mobileQuery?.matches ?? false;
    const containers = this.querySelectorAll('.fg-story__media-item, .fg-story__step');
    for (const container of containers) {
      const video = container.querySelector('video');
      if (!video) continue;
      const isStep = container.classList.contains('fg-story__step');
      const shouldPlay = isStep === mobile && container.classList.contains('is-active');
      if (shouldPlay) video.play().catch(() => {});
      else video.pause();
    }
  };

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mobileQuery?.removeEventListener('change', this.#syncVideos);
  }
}

if (!customElements.get('fg-scroller')) customElements.define('fg-scroller', FgScroller);
if (!customElements.get('fg-story')) customElements.define('fg-story', FgStory);

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[data-fg-dropdown]') : null;
  if (!(link instanceof HTMLElement)) return;

  event.preventDefault();

  // Open the submenu the same way hovering does.
  const menu = /** @type {any} */ (link.closest('header-menu'));
  if (typeof menu?.activate === 'function') menu.activate({ target: link.closest('li') });
  link.focus();
});
